import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, Loader2, Clock, XCircle, MessageCircle } from "lucide-react";

const GREEN = "#00A651";
const YELLOW = "#FFCC00";
const WA_NUMBER = "32489299765"; // numéro Direction (reçoit les transferts)

const fmtFcfa = (n) => `${Math.round(Number(n) || 0).toLocaleString("fr-FR")} FCFA`;

export default function PaySuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | expired | error
  const [order, setOrder] = useState(null);

  useEffect(() => {
    document.head.querySelector('#poppins-pay') || (() => {
      const l = document.createElement("link"); l.id = "poppins-pay"; l.rel = "stylesheet";
      l.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;600;700;800;900&display=swap";
      document.head.appendChild(l);
    })();
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/pay/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("expired"); return; }
      } catch { /* keep polling */ }
      if (attempts >= 10) { setState("error"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div data-testid="pay-success" className="max-w-md w-full rounded-3xl p-8 border border-white/10 bg-[#0d0d0d] text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 mx-auto animate-spin" style={{ color: YELLOW }} />
            <h1 className="mt-5 font-black text-2xl">Vérification du paiement…</h1>
            <p className="mt-2 text-white/60 text-sm">Un instant, on confirme ta transaction.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center" style={{ background: GREEN }}>
              <CheckCircle2 className="w-9 h-9 text-black" />
            </div>
            <h1 data-testid="pay-success-title" className="mt-5 font-black text-2xl">Paiement reçu. Envoi en 5min ⏱️</h1>
            <p className="mt-2 text-white/60 text-sm">Ton proche recevra l'argent dans quelques minutes. Une capture preuve te sera envoyée.</p>
            {order && (
              <div className="mt-6 rounded-2xl p-4 text-left space-y-2" style={{ background: "rgba(0,166,81,0.1)" }}>
                <div className="flex justify-between text-sm"><span className="text-white/50">Montant reçu</span><span className="font-black" style={{ color: GREEN }}>{fmtFcfa(order.fcfa_recu)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-white/50">Bénéficiaire</span><span className="font-semibold">{order.num_beneficiaire}</span></div>
                <div className="flex justify-between text-sm"><span className="text-white/50">Réseau</span><span className="font-semibold">{order.reseau === "MoMo" ? "MTN MoMo" : "Orange Money"}</span></div>
              </div>
            )}
            <button data-testid="pay-notify-wa" onClick={() => {
              const msg = `💸 Mboa Go Pay — Nouveau transfert payé\nNom: ${order?.nom_client || "-"}\nMontant: ${Number(order?.montant_eur || 0)}€ → ${fmtFcfa(order?.fcfa_recu)}\nBénéficiaire: ${order?.num_beneficiaire || "-"} (${order?.reseau === "MoMo" ? "MTN MoMo" : "Orange Money"})`;
              window.open(`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`, "_blank");
            }} className="mt-6 w-full h-13 py-3.5 rounded-xl font-extrabold text-white inline-flex items-center justify-center gap-2" style={{ background: "#25D366" }}>
              <MessageCircle className="w-5 h-5" /> Prévenir sur WhatsApp
            </button>
            <p className="mt-2 text-xs text-white/40">Clique pour confirmer ton envoi à notre équipe et recevoir la preuve.</p>
            <button data-testid="pay-success-home" onClick={() => navigate("/pay")} className="mt-4 w-full h-12 rounded-xl font-bold text-black" style={{ background: YELLOW }}>Faire un autre envoi</button>
          </>
        )}
        {state === "expired" && (
          <>
            <Clock className="w-14 h-14 mx-auto" style={{ color: YELLOW }} />
            <h1 className="mt-5 font-black text-2xl">Paiement non finalisé</h1>
            <p className="mt-2 text-white/60 text-sm">La session a expiré ou le paiement a échoué.</p>
            <button onClick={() => navigate("/pay")} className="mt-6 w-full h-12 rounded-xl font-bold text-black" style={{ background: GREEN }}>Réessayer</button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 mx-auto text-white/40" />
            <h1 className="mt-5 font-black text-2xl">Impossible de vérifier</h1>
            <p className="mt-2 text-white/60 text-sm">Contacte le support si tu as été débité.</p>
            <button onClick={() => navigate("/pay")} className="mt-6 w-full h-12 rounded-xl font-bold text-black" style={{ background: GREEN }}>Retour</button>
          </>
        )}
      </div>
    </div>
  );
}
